import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { CoverSurface } from '@/components/CoverSurface';
import { HaiIcon } from '@/components/icons';
import type { BrandKit, Project } from '@/types';

import { projectCoverFallback, projectMetaLine } from '../../homeMeta';

// =============================================================================
// HomeRecentProjects
// =============================================================================

export interface HomeRecentProjectsProps {
  projects: Project[] | null;
  brandKitById: Map<string, BrandKit>;
  loadError: string | null;
}

/**
 * 3 欄 grid:最多 5 張 project card +「建立新專案」tile **緊接在最後一張卡後面**
 * (R279 / FB-20260821-0005 — 舊版用空 pad 把 CTA 釘在 index 5 右下角,專案不滿
 * 5 張時中間空一排、CTA 沉到第二列;現與 /projects 列表頁同行為)。
 * Loading state 顯 6 個 skeleton cells;載入失敗顯 banner + 不影響 CTA。
 *
 * Project cover 配色:用 projectId 字串 hash mod 4 決定(deterministic);若有
 * brand kit primary color 優先 override 讓品牌一致性 win。
 */
export function HomeRecentProjects({
  projects,
  brandKitById,
  loadError,
}: HomeRecentProjectsProps) {
  const { t } = useTranslation('home');
  const recent = (projects ?? []).slice(0, 5);

  return (
    <section data-testid="home-recent-projects">
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 12,
        }}
      >
        <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--ink-100)', margin: 0 }}>
          {t('recentProjects.title')}
        </h2>
        {/* D3 `.seclink`:teal-700 字 + pgNext 圖示(不用 unicode →;--tide-600 當字只有 3.7:1) */}
        <Link
          to="/projects"
          data-testid="home-recent-projects-view-all"
          style={{
            fontSize: 12,
            color: 'var(--concept-fg)',
            fontWeight: 700,
            textDecoration: 'none',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 3,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.textDecoration = 'underline';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.textDecoration = 'none';
          }}
        >
          {t('recentProjects.viewAll')}
          <HaiIcon name="pgNext" size={12} />
        </Link>
      </div>

      {loadError && (
        <div
          role="alert"
          data-testid="home-projects-load-error"
          style={{
            padding: 12,
            borderRadius: 8,
            // 載入失敗 = 錯誤 → rose(CIS;琥珀只給場景 / 警示)
            background: 'var(--rose-50)',
            border: '1px solid var(--rose-200)',
            color: 'var(--rose-700)',
            fontSize: 12,
            marginBottom: 12,
          }}
        >
          {t('projects.loadError')}
        </div>
      )}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
          gap: 12,
        }}
      >
        {projects === null ? (
          Array.from({ length: 6 }).map((_, i) => <ProjectCardSkeleton key={i} />)
        ) : (
          <>
            {recent.map((p) => (
              <ProjectCard
                key={p.projectId}
                project={p}
                brandKit={p.brandKitId ? brandKitById.get(p.brandKitId) ?? null : null}
              />
            ))}
            {/* R279(FB-20260821-0005「建立專案的圖標位置略怪」)— 原本用空 pad 把 CTA
                固定在 index 5(右下角):專案不滿 5 張時中間隔著整排空格,虛線卡孤零零
                沉在右下(空 pad 高度 0,CTA 還會掉到比第一列卡片矮的第二列)。改成
                緊接在最後一張專案卡後面 —— 與 /projects 列表頁的虛線卡同行為。 */}
            <NewProjectTile />
          </>
        )}
      </div>
    </section>
  );
}

function ProjectCard({
  project,
  brandKit,
}: {
  project: Project;
  brandKit: BrandKit | null;
}) {
  const { t } = useTranslation('home');
  const coverFallback = projectCoverFallback(project, brandKit);
  return (
    <Link
      to={`/canvas/${project.projectId}`}
      data-testid={`home-recent-project-${project.projectId}`}
      style={{
        background: 'var(--ink-900)',
        border: '1px solid var(--hairline)',
        borderRadius: 12,
        overflow: 'hidden',
        textDecoration: 'none',
        color: 'inherit',
        transition: 'transform .12s, box-shadow .12s',
        display: 'block',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = 'var(--shadow-md)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = '';
        e.currentTarget.style.boxShadow = '';
      }}
    >
      {/* R245 — 封面三態;未設(none)才落回 projectCoverFallback 的品牌色漸層 */}
      <CoverSurface
        data-testid={`home-recent-project-cover-${project.projectId}`}
        cover={project.cover}
        fallback={coverFallback}
        style={{ aspectRatio: '5 / 3' }}
      />
      <div style={{ padding: 12 }}>
        <div
          style={{
            fontSize: 12.5,
            fontWeight: 600,
            color: 'var(--ink-100)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {project.name}
        </div>
        <div
          style={{
            // D3 `.pj-meta`:11.5 / 次文字 / sans + tabular-nums(CIS 最小 10.5、中文不吃 mono)
            fontSize: 11.5,
            color: 'var(--ink-500)',
            marginTop: 2,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {/* TODO(BE): asset count per project (M-04 batch / dagService nodes count) */}
          <span data-testid={`home-recent-project-${project.projectId}-meta`}>
            {projectMetaLine(project, t)}
          </span>
        </div>
      </div>
    </Link>
  );
}

function ProjectCardSkeleton() {
  return (
    <div
      aria-hidden
      style={{
        background: 'var(--ink-850)',
        border: '1px solid var(--hairline)',
        borderRadius: 12,
        overflow: 'hidden',
      }}
    >
      <div style={{ aspectRatio: '5 / 3', background: 'var(--ink-800)' }} />
      <div style={{ padding: 12 }}>
        <div
          style={{
            height: 12,
            background: 'var(--hairline)',
            borderRadius: 4,
            marginBottom: 6,
          }}
        />
        <div
          style={{ height: 8, width: '60%', background: 'var(--hairline)', borderRadius: 4 }}
        />
      </div>
    </div>
  );
}

function NewProjectTile() {
  const { t } = useTranslation('home');
  return (
    <Link
      to="/new"
      data-testid="home-recent-projects-new-tile"
      style={{
        border: '2px dashed var(--hairline-strong)',
        borderRadius: 12,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        color: 'var(--ink-500)',
        textDecoration: 'none',
        transition: 'border-color .12s, color .12s',
        minHeight: 160,
      }}
      onMouseEnter={(e) => {
        // 框線 teal-600;字走 --concept-fg(teal 當字的 token,--tide-600 只當框線 / 填色)
        e.currentTarget.style.borderColor = 'var(--tide-600)';
        e.currentTarget.style.color = 'var(--concept-fg)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = 'var(--hairline-strong)';
        e.currentTarget.style.color = 'var(--ink-500)';
      }}
    >
      <div
        style={{
          width: 40,
          height: 40,
          borderRadius: 999,
          background: 'var(--ink-800)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <HaiIcon name="plus" size={18} />
      </div>
      <span style={{ fontSize: 12, fontWeight: 500 }}>{t('recentProjects.newTile')}</span>
    </Link>
  );
}
